import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Breadcrumb, BreadcrumbItem, Card, Row, Col, Dropdown, Spinner, Form } from 'react-bootstrap';
import { AuthService, EventsService, CustomerService, TransRoutesService, ResourceService } from '../../services';
import DashboardCustomersList from './DashboardCustomersList';
import moment from 'moment';
import './Dashboard.css';
import { readPageUiObject, writePageUiObject } from '../../shared/pageFilterPersistence';

const Dashboard = () => {
  const navigate = useNavigate();
  const [todayAttendance, setTodayAttendance] = useState(0);
  const [todayMedicalAppointments, setTodayMedicalAppointments] = useState(0);
  const [membersCount, setMembersCount] = useState(0);
  const [visitorsCount, setVisitorsCount] = useState(0);
  const [medicalPercentage, setMedicalPercentage] = useState(0);
  const [medicalTrend, setMedicalTrend] = useState(''); // 'increase' or 'decrease'
  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [groupedEvents, setGroupedEvents] = useState(new Map());
  const [selectedEventType, setSelectedEventType] = useState('medical');
  const [customers, setCustomers] = useState([]);
  const [resources, setResources] = useState([]);
  const [showSpinner, setShowSpinner] = useState(false);
  const [showDeletedEvents, setShowDeletedEvents] = useState(false);
  const [eventsSidebarUiReady, setEventsSidebarUiReady] = useState(false);
  const customersPanelRef = useRef(null);
  const [eventsPanelHeightPx, setEventsPanelHeightPx] = useState(null);
  const canViewCustomers = AuthService.canViewCustomers();

  useLayoutEffect(() => {
    if (!canViewCustomers) {
      setEventsPanelHeightPx(null);
      return;
    }
    const el = customersPanelRef.current;
    if (!el) return;
    const sync = () => {
      const h = el.getBoundingClientRect().height;
      setEventsPanelHeightPx(Number.isFinite(h) ? Math.round(h) : null);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    window.addEventListener('resize', sync);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', sync);
    };
  }, [canViewCustomers]);

  useEffect(() => {
    const saved = readPageUiObject("dashboard");
    if (saved) {
      if (typeof saved.showDeletedEvents === "boolean") setShowDeletedEvents(saved.showDeletedEvents);
      if (typeof saved.selectedEventType === "string") setSelectedEventType(saved.selectedEventType);
    }
    setEventsSidebarUiReady(true);
  }, []);

  const eventTypes = [
    { value: 'medical', label: 'Medical Appointments' },
    { value: 'activity', label: 'Activities' },
    { value: 'incident', label: 'Attendance' },
    { value: 'meal_plan', label: 'Meal Plan' },
    { value: 'reminder', label: 'Important Dates' }
  ];

  const allowedEventTypes = eventTypes.filter((eventType) => {
    switch (eventType.value) {
      case 'medical':
        return AuthService.canViewMedicalEvents();
      case 'activity':
        return (
          AuthService.canViewCalendarTab('activitiesCalendar') ||
          AuthService.canEditCalendarTab('activitiesCalendar')
        );
      case 'incident':
        return AuthService.canViewAttendance();
      case 'meal_plan':
        return (
          AuthService.canViewCalendarTab('mealPlanCalendar') ||
          AuthService.canEditCalendarTab('mealPlanCalendar')
        );
      case 'reminder':
        return (
          AuthService.canViewCalendarTab('reminderDatesCalendar') ||
          AuthService.canEditCalendarTab('reminderDatesCalendar')
        );
      default:
        return false;
    }
  });

  const effectiveSelectedEventType =
    allowedEventTypes.find((t) => t.value === selectedEventType)?.value ??
    allowedEventTypes[0]?.value ??
    '';

  const dashboardUiSnapshot = useMemo(
    () => ({ showDeletedEvents, selectedEventType }),
    [showDeletedEvents, selectedEventType]
  );
  useEffect(() => {
    if (!eventsSidebarUiReady) return;
    const t = setTimeout(() => {
      writePageUiObject("dashboard", dashboardUiSnapshot);
    }, 400);
    return () => clearTimeout(t);
  }, [eventsSidebarUiReady, dashboardUiSnapshot]);

  /** Match Center Calendar medical list: inactive status or gray = deleted/disabled. */
  const eventPassesDeletedFilter = (item) => {
    if (showDeletedEvents) return true;
    const gray = `${item?.color ?? ''}`.toLowerCase() === 'gray';
    return item?.status === 'active' && !gray;
  };

  // Fetch today's events for the calendar list
  const fetchTodayEvents = async (eventType) => {
    try {
      const today = new Date();
      const fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
      const toDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      
      const eventsData = await EventsService.getAllEvents({ 
        from: EventsService.formatDate(fromDate), 
        to: EventsService.formatDate(toDate) 
      });
      setAllEvents(eventsData.data);
      
      let processedEvents = [];
      
      // Filter and map events based on selected type
      if (eventType === 'medical') {
        if (customers?.length > 0 && resources.length > 0) {
          const originalEvents = [...eventsData.data];
          processedEvents = originalEvents
            ?.filter(item => item.type === 'medical')
            ?.map((item) => {
              const customerField = item?.data?.customer ? (customers?.find(c => c.id === item?.data?.customer)?.name || item?.data?.client_name || '') : (item?.data?.client_name || '');
              const doctorField = item?.data?.resource ? ((resources?.find(r => r.id === item?.data?.resource))?.name || item?.data?.resource_name || '') : (item?.data?.resource_name || '');
              item.event_id = item.id;
              item.customer = customerField;
              item.doctor = doctorField;
              item.phone = item?.data?.resource ? ((resources?.find(r => r.id === item?.data?.resource))?.phone || item?.data?.resource_phone || '') : (item?.data?.resource_phone || '');
              item.contact = item?.data?.resource? ((resources?.find(r => r.id === item?.data?.resource))?.contact || item?.data?.resource_contact || '') : (item?.data?.resource_contact || '');
              item.fax = item?.data?.resource ? ((resources?.find(r => r.id === item?.data?.resource))?.fax || item?.data?.resource_fax || '') : (item?.data?.resource_fax || '');
              item.address = item?.data?.resource ? ((resources?.find(r => r.id === item?.data?.resource))?.address || item?.data?.resource_address || '') : (item?.data?.resource_address || '');
              item.translation = EventsService.getMedicalLanguageSupportDisplay(item?.data?.interpreter || '');
              item.newPatient = item?.data?.new_patient || '';
              item.needId = item?.data?.need_id || '';
              item.disability = item?.data?.disability || '';
              item.startTime = item?.start_time? `${moment(new Date(item?.start_time)).format('YYYY-MM-DD HH:mm')}` : '';
              item.endTime = item?.start_time? `${moment(new Date(item?.end_time)).format('YYYY-MM-DD HH:mm')}` : '';
              item.fasting = item?.data?.fasting || '';
              item.transportation = item?.link_event_name || '';
              item.title = `${customerField}, provider: ${doctorField}`;
              item.start = item?.start_time? `${moment(new Date(item?.start_time)).format('YYYY-MM-DD HH:mm')}` : `${moment().format('YYYY-MM-DD HH:mm')}`;
              item.end = item?.stop_time? `${moment(new Date(item?.stop_time)).format('YYYY-MM-DD HH:mm')}` : (item?.start_time? `${moment(item?.start_time).format('YYYY-MM-DD HH:mm')}` : `${moment().format('YYYY-MM-DD HH:mm')}`);
              item.color = item?.color;
              item._options = { additionalClasses: [`event-${item?.color || 'primary'}`]};
              return item;
            });
        }
      } else {
        const originalEvents = [...eventsData.data];
        processedEvents = originalEvents
          ?.filter(item => item.type === eventType)
          ?.map(item => ({
            ...item,
            title: item?.title,
            start: item?.start_time? `${moment(new Date(item?.start_time)).format('YYYY-MM-DD HH:mm')}` : `${moment().format('YYYY-MM-DD HH:mm')}`,
            end: item?.stop_time? `${moment(new Date(item?.stop_time)).format('YYYY-MM-DD HH:mm')}` : (item?.start_time? `${moment(item?.start_time).format('YYYY-MM-DD HH:mm')}` : `${moment().format('YYYY-MM-DD HH:mm')}`),
            _options: { additionalClasses: [`event-${item?.color || 'primary'}`]}
          }));
      }

      processedEvents = (processedEvents || []).filter(eventPassesDeletedFilter);

      const sortedEvents = [...(processedEvents || [])].sort((a, b) => {
        const aTime = moment(a?.start_time).valueOf();
        const bTime = moment(b?.start_time).valueOf();
        return aTime - bTime;
      });

      setEvents(sortedEvents);
      
      // Group events by date
      const eventsDateMap = new Map();
      sortedEvents?.forEach(eventItem => {
        const dateString = moment(eventItem.start_time).format('MMM Do, YYYY');
        if (eventsDateMap.has(dateString)) {
          eventsDateMap.set(dateString, [...eventsDateMap.get(dateString), eventItem]);
        } else {
          eventsDateMap.set(dateString, [eventItem]);
        }
      });

      eventsDateMap.forEach((items, key) => {
        const sortedItems = [...items].sort((a, b) => {
          const aTime = moment(a?.start_time).valueOf();
          const bTime = moment(b?.start_time).valueOf();
          return aTime - bTime;
        });
        eventsDateMap.set(key, sortedItems);
      });

      setGroupedEvents(eventsDateMap);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  // Today's attendance = inbound customers marked In Center (same rules as Transportation header stats).
  // scheduleDate must be MM/DD/YYYY — same as fetchRoutesSaga / DB schedule_date (not YYYY-MM-DD).
  const fetchTodayAttendance = async () => {
    try {
      const today = moment(new Date()).format('MM/DD/YYYY');
      const routesData = await TransRoutesService.getAll(today);
      const routes = Array.isArray(routesData?.data) ? routesData.data : [];
      const inboundRoutes = routes.filter((route) => route.type === 'inbound');
      const stats = TransRoutesService.getRouteSectionManifestStats(inboundRoutes);
      setTodayAttendance(stats.checkedInCount);
      setMembersCount(stats.checkedInMembers);
      setVisitorsCount(stats.checkedInVisitors);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
  };

  // Fetch today's and yesterday's medical appointments
  const fetchMedicalAppointments = async () => {
    try {
      const today = EventsService.formatDate(new Date());
      const yesterday = EventsService.formatDate(new Date(Date.now() - 24 * 60 * 60 * 1000));
      
      // Get today's medical events
      const todayEventsData = await EventsService.getAllEvents({ date: today });
      const todayMedicalEvents = todayEventsData.data.filter(event => event.type === 'medical');
      
      // Get yesterday's medical events
      const yesterdayEventsData = await EventsService.getAllEvents({ date: yesterday });
      const yesterdayMedicalEvents = yesterdayEventsData.data.filter(event => event.type === 'medical');
      
      setTodayMedicalAppointments(todayMedicalEvents.length);
      
      // Calculate percentage change
      if (yesterdayMedicalEvents.length > 0) {
        const percentageChange = ((todayMedicalEvents.length - yesterdayMedicalEvents.length) / yesterdayMedicalEvents.length) * 100;
        setMedicalPercentage(Math.abs(percentageChange));
        setMedicalTrend(percentageChange >= 0 ? 'increase' : 'decrease');
      } else if (todayMedicalEvents.length > 0) {
        setMedicalPercentage(100);
        setMedicalTrend('increase');
      } else {
        setMedicalPercentage(0);
        setMedicalTrend('');
      }
    } catch (error) {
      console.error('Error fetching medical appointments:', error);
    }
  };

  useEffect(() => {
    if (!AuthService.canViewDashboard()) {
      navigate('/home-empty');
      return;
    }
    setShowSpinner(true);
    Promise.all([
      fetchTodayAttendance(),
      fetchMedicalAppointments(),
      CustomerService.getAllCustomers().then((data) => {
        setCustomers(data.data);
      }),
      ResourceService.getAll().then((data) => {
        setResources(data.data);
      })
    ]).finally(() => {
      setShowSpinner(false);
    });
  }, [navigate]);

  useEffect(() => {
    if (allowedEventTypes.length === 0) return;
    if (selectedEventType !== effectiveSelectedEventType) {
      setSelectedEventType(effectiveSelectedEventType);
    }
  }, [allowedEventTypes.length, selectedEventType, effectiveSelectedEventType]);

  // Separate useEffect for events that depends on effective type, customers, and resources
  useEffect(() => {
    if (!effectiveSelectedEventType) {
      setEvents([]);
      setGroupedEvents(new Map());
      return;
    }
    if (customers?.length > 0 && resources?.length > 0) {
      fetchTodayEvents(effectiveSelectedEventType);
    }
  }, [effectiveSelectedEventType, customers, resources, showDeletedEvents]);

  return (
    <>
      {showSpinner && <div className="spinner-overlay">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>}
      <div className="list row mb-4">
        <Breadcrumb>
          <Breadcrumb.Item>Dashboard</Breadcrumb.Item>
          <Breadcrumb.Item active>
            Dashboard
          </Breadcrumb.Item>
        </Breadcrumb>
        <div className="col-md-12 text-primary">
          <h4>Dashboard</h4>
        </div>
      </div>

      <div className="app-main-content-list-container dashboard-page">
        {/* Full-width KPI row so the event panel shares a row only with the customer list (aligned bottoms) */}
        <div className="row mb-4 dashboard-top-cards">
          <div className="col-12">
            <div className="row">
              <div className="col-md-6">
                <Card className="h-100 dashboard-card">
                  <Card.Body>
                    <Card.Title className="dashboard-card-title">Today's Attendance</Card.Title>
                    <Card.Text className="h2 text-primary">
                      {todayAttendance}
                    </Card.Text>
                    <Card.Text className="text-muted">
                      {membersCount} Members • {visitorsCount} Visitors
                    </Card.Text>
                  </Card.Body>
                </Card>
              </div>
              <div className="col-md-6">
                <Card className="h-100 dashboard-card">
                  <Card.Body>
                    <Card.Title className="dashboard-card-title">Today's Medical Appointments</Card.Title>
                    <Card.Text className="h2 text-primary">
                      {todayMedicalAppointments}
                    </Card.Text>
                    <Card.Text className="text-muted">
                      {medicalTrend && (
                        <span className={medicalTrend === 'increase' ? 'text-success' : 'text-danger'}>
                          {medicalTrend === 'increase' ? '↗' : '↘'} {medicalPercentage.toFixed(1)}% {medicalTrend === 'increase' ? 'increase' : 'decrease'} from yesterday
                        </span>
                      )}
                      {!medicalTrend && 'No change from yesterday'}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </div>
            </div>
          </div>
        </div>

        <div className="row dashboard-main-panels">
          {canViewCustomers && (
            <div className="col-md-9 dashboard-customers-panel" ref={customersPanelRef}>
              <DashboardCustomersList freezeTableHeader />
            </div>
          )}

          <div
            className={`dashboard-right-sidebar d-flex flex-column overflow-hidden ${
              canViewCustomers ? 'col-md-3' : 'col-12 col-lg-3 ms-lg-auto'
            }`}
            style={
              canViewCustomers && eventsPanelHeightPx != null
                ? { height: eventsPanelHeightPx, maxHeight: eventsPanelHeightPx, minHeight: 0 }
                : undefined
            }
          >
            <div className="column-container dashboard-column-container">
              <div className="column-card dashboard-events-column-card">
                <div className="mb-3 flex-shrink-0">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="text-primary mb-0">List</h6>
                    <select 
                      value={effectiveSelectedEventType} 
                      onChange={(e) => setSelectedEventType(e.target.value)}
                      className="form-select form-select-sm dashboard-event-selector"
                      disabled={allowedEventTypes.length === 0}
                    >
                      {allowedEventTypes.map((eventType) => (
                        <option key={eventType.value} value={eventType.value}>
                          {eventType.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Form.Check
                    type="checkbox"
                    id="dashboard-show-deleted-events"
                    className="small"
                    label="Show deleted / inactive"
                    checked={showDeletedEvents}
                    onChange={(e) => setShowDeletedEvents(e.target.checked)}
                  />
                </div>
                <div className="dashboard-events-scroll">
                  {Array.from(groupedEvents?.keys())?.map((key) => {
                    return (
                      <div key={key}>
                        <h6 className="text-primary me-2 dashboard-event-date">{key}</h6>
                        {groupedEvents.get(key).map((eventItem, index) => (
                          <div key={index} className={`event-${eventItem.color || 'primary'} mb-3 event-list-item-container dashboard-event-item`}>
                            <div className="event-item-flex">
                              <div className="sx__month-agenda-event__title dashboard-event-title">
                                {effectiveSelectedEventType === 'medical' ? eventItem.customer || eventItem.title : eventItem.title}
                              </div>
                              <div className="sx__event-modal__time dashboard-event-time">
                                {effectiveSelectedEventType === 'medical' ? (
                                  <>
                                    {moment(eventItem?.start_time).format('hh:mm A')}
                                    {eventItem?.end_time
                                      ? ` – ${moment(eventItem?.end_time).format('hh:mm A')}`
                                      : ''}
                                  </>
                                ) : effectiveSelectedEventType === 'meal_plan' && eventItem?.meal_type ? (
                                  String(eventItem.meal_type).replace(/_/g, ' ')
                                ) : null}
                              </div>
                            </div>
                            {effectiveSelectedEventType === 'medical' ? (
                              <>
                                <div
                                  className="sx__event-modal__time with-padding dashboard-event-description"
                                  style={{ fontWeight: 600, fontSize: '15px' }}
                                >
                                  Provider: {eventItem?.doctor || '-'}
                                </div>
                                <div className="sx__event-modal__time with-padding dashboard-event-description">
                                  Contact: {eventItem?.phone || '-'}
                                </div>
                                <div className="sx__event-modal__time with-padding dashboard-event-description">
                                  Secondary: {eventItem?.contact || '-'}
                                </div>
                                <div className="sx__event-modal__time with-padding dashboard-event-description">
                                  Fax: {eventItem?.fax || '-'}
                                </div>
                                <div className="sx__event-modal__time with-padding dashboard-event-description">
                                  Address: {eventItem?.address || '-'}
                                </div>
                                <div className="sx__event-modal__time with-padding dashboard-event-description">
                                  Language Support: {EventsService.getMedicalLanguageSupportDisplay(eventItem?.translation || eventItem?.data?.interpreter) || '-'}
                                </div>
                              </>
                            ) : (
                              <div className="sx__event-modal__time with-padding dashboard-event-description">
                                {eventItem?.description || 'No description'}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                  {groupedEvents.size === 0 && (
                    <div className="text-muted text-center py-3">
                      {allowedEventTypes.length === 0
                        ? 'No calendar list available for your account'
                        : effectiveSelectedEventType === 'incident'
                          ? 'No attendance notes available'
                          : `No ${(eventTypes.find(t => t.value === effectiveSelectedEventType)?.label || effectiveSelectedEventType).toLowerCase()} available`}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard; 